import React, { useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';

import {
  SectionContent,
  PageHeader,
  InfoCard,
  ImageTextOverlay,
  ScrollableJobs
} from '../../components';
import {
  HOW_TO_JOIN_CONTENT,
  INTERVIEW_CONTENT,
  INTERNSHIP_CONTENT
} from '../../data';
import { jobData } from '../../data/jobs/jobData';
import ROUTES from '../../config/routes';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import internshipImage from '../../assets/images/how-to-join/internship-image-01.jpeg';
import interviewImage from '../../assets/images/how-to-join/interview-image-01.jpeg';
import mqcapitalImage11 from '../../assets/images/how-to-join/mqcapital-image-11.jpg';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const HowToJoinPage = () => {
  // State for job counts
  // eslint-disable-next-line no-unused-vars
  const [visibleJobCount, setVisibleJobCount] = useState(4);

  return (
    <>
      <SEO
        title={seoData.howToJoin.title}
        content={seoData.howToJoin.content}
        keywords={seoData.howToJoin.keywords}
        canonical={seoData.howToJoin.canonical}
        ogImage={seoData.howToJoin.ogImage}
      />
      {/* Main "How to Join" section */}}
      <PageHeader
        id='hero'
        title={HOW_TO_JOIN_CONTENT.TITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />
      <ImageTextOverlay
        imageSrc={mqcapitalImage11}
        text={HOW_TO_JOIN_CONTENT.INTRO}
        height={{ xs: '400px', sm: '300px', md: '300px', lg: '300px' }}
        boxSx={{
          width: '100%',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          textAlign: 'center'
        }}
        linkMappings={[
          {
            word: 'join us',
            url: '#chooseYourPath',
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'none',
            newTab: false
          }
        ]}
        textSx={{
          fontSize: {
            xs: '1.05rem',
            sm: '1.15rem',
            md: '1.3rem',
            lg: '1.4rem'
          },
          lineHeight: 1.6,
          color: THEME.COLORS.NEUTRAL_WHITE,
          px: { xs: 2, sm: 3, md: 5 },
          zIndex: 2,
          width: '100%',
          maxWidth: { xs: '100%', md: '80%' }
        }}
        // Remove height/minHeight from containerSx!
        containerSx={{ position: 'relative' }}
        overlayColor='rgba(0,0,0,0.3)'
      />
      {/* Career Development Section */}
      <SectionContent
        id='careerDevelopment'
        title={HOW_TO_JOIN_CONTENT.SECTIONS.CAREER_DEVELOPMENT.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        linkMappings={[
          {
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'underlined',
            newTab: false,
            word: 'click here',
            url: '/how-to-join/eu-recruitment-policy'
          },
          {
            word: 'click here ',
            url: '/how-to-join/uk-recruitment-policy',
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'underlined',
            newTab: false
          }
        ]}
        text={[
          {
            text: HOW_TO_JOIN_CONTENT.SECTIONS.CAREER_DEVELOPMENT.TEXT[0],
            fontSize: '1.3rem'
          },
          {
            text: HOW_TO_JOIN_CONTENT.SECTIONS.CAREER_DEVELOPMENT.HIGHLIGHT,
            textAlign: 'center',
            padding: `${THEME.SPACING.XL} ${THEME.SPACING.XL}`,
            backgroundColor: THEME.COLORS.NEUTRAL_BLACK,
            color: THEME.COLORS.NEUTRAL_WHITE,
            hoverColor: THEME.COLORS.PRIMARY_BLUE,
            marginTop: ELEMENT_SPACING,
            fontSize: '1.3rem'
          },
          {
            text: HOW_TO_JOIN_CONTENT.SECTIONS.CAREER_DEVELOPMENT.DISCLAIMER,
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Choose Your Path Section */}
      <SectionContent
        id='chooseYourPath'
        title={HOW_TO_JOIN_CONTENT.SECTIONS.CHOOSE_YOUR_PATH.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
      />
      {/* Job Listing Section */}
      <Box sx={{ mb: SECTION_SPACING * 1.5 }}>
        <ScrollableJobs
          jobs={jobData}
          setJobCount={setVisibleJobCount}
          jobType='Jobs'
        />
      </Box>
      {/* Learn more cards */}
      <Box
        sx={{
          mt: SECTION_SPACING,
          mb: SECTION_SPACING
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <InfoCard
              variant='elevated'
              title={INTERVIEW_CONTENT.TITLE}
              description={INTERVIEW_CONTENT.DESCRIPTION}
              imageSrc={interviewImage}
              imageAlt='Interview Process'
              linkTo={ROUTES.HOW_TO_JOIN.INTERVIEW}
              imageSx={{
                height: 250,
                objectFit: 'cover',
                objectPosition: 'center'
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoCard
              variant='elevated'
              title={INTERNSHIP_CONTENT.TITLE}
              description={INTERNSHIP_CONTENT.DESCRIPTION}
              imageSrc={internshipImage}
              imageAlt='Internship Opportunities'
              linkTo={ROUTES.HOW_TO_JOIN.INTERNSHIP}
              imageSx={{
                height: 250,
                objectFit: 'cover',
                objectPosition: 'center'
              }}
            />
          </Grid>
        </Grid>
      </Box>
      {/* Footer note with separator line */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {HOW_TO_JOIN_CONTENT.FOOTER_NOTE}
        </Typography>
      </Box>
    </>
  );
};

export default HowToJoinPage;
